import React, { FC, useRef } from 'react'
import Heading from '../../../atoms/Heading/Heading'
import { motion, useInView } from 'framer-motion'

interface ReliabilityAppsSupportStepsProps {
    appSupportData: any
}

const ReliabilityAppsSupportSteps: FC<ReliabilityAppsSupportStepsProps> = ({ appSupportData }) => {
    // Ref for scroll animation
    const containerRef = useRef(null);
    
    // Use inView hook for scroll animation
    const isContainerInView = useInView(containerRef, { once: false, amount: 0.2 });
    
    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2,
                delayChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { y: 30, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: { 
                duration: 0.5, 
                ease: "easeOut" 
            }
        }
    };

    const numberVariants = {
        hidden: { scale: 0.8, opacity: 0 },
        visible: { 
            scale: 1, 
            opacity: 1,
            transition: { 
                duration: 0.4,
                ease: "easeOut",
                type: "spring",
                stiffness: 200
            } 
        }
    };

    return (
        <div className='p-4 sm:p-6 md:p-8 pt-8 md:pt-12'>
            <Heading subTitle='Reliability in every step' />
            <motion.div 
                ref={containerRef}
                className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-8 md:gap-12 p-4 sm:p-8 md:p-20 pt-8 md:pt-16 px-4 sm:px-8 md:px-36'
                variants={containerVariants}
                initial="hidden"
                animate={isContainerInView ? "visible" : "hidden"}
            >
                {appSupportData.ReliabilityAppsSupportData.map((reliable: any, index: number) => {
                    return (
                        <motion.div 
                            key={index} 
                            className='rounded-3xl pt-[2px] pl-[1px] hover:pl-[0px] hover:pt-[0px] transition-all duration-300 mb-6 sm:mb-4 md:mb-0' 
                            style={{
                                backgroundImage: `linear-gradient(132.31deg, ${reliable.bgColor} 0%, rgba(132, 44, 255, 0) 33.33%)`,
                                borderImageSlice: 1,
                            }}
                            variants={itemVariants}
                            whileHover={{ scale: 1.03 }}
                        >
                            <div className="flex flex-col items-start justify-center p-4 sm:p-6 md:p-8 bg-blackDark !rounded-3xl relative h-auto sm:h-52 md:h-60 inset-0"
                            >
                                <motion.div 
                                    className={`text-4xl sm:text-5xl md:text-7xl font-extrabold bg-gradient-to-b ${reliable.fromColor} to-[rgba(152,255,18,0)] bg-clip-text text-transparent font-black`}
                                    variants={numberVariants}
                                >
                                    0{reliable.key}
                                </motion.div>
                                <div className='-mt-2 sm:-mt-3 md:-mt-4'>
                                    <motion.div 
                                        className={`text-base sm:text-lg md:text-xl text-start font-semibold text-${reliable.color} mb-1`}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ delay: 0.3 + (index * 0.1), duration: 0.5 }}
                                    >
                                        {reliable.title}
                                    </motion.div>
                                    <motion.div 
                                        className="text-white text-start text-sm sm:text-base md:text-md pr-2 sm:pr-4 md:pr-8"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ delay: 0.4 + (index * 0.1), duration: 0.5 }}
                                    >
                                        {reliable.subtitle}
                                    </motion.div>
                                </div>
                            </div>
                        </motion.div>
                    )
                })}
            </motion.div>
        </div>
    )
}

export default ReliabilityAppsSupportSteps
